import React, { useState, useEffect } from 'react';
import Image from 'next/image';

const ContentAnimation: React.FC<{
  children: React.ReactNode;
  isOpen: boolean;
}> = ({ children, isOpen }) => {
  const baseStyles = 'overflow-hidden';
  const animationStyles = isOpen ? 'animate-expand origin-top' : '';

  return (
    <div className={`${baseStyles} ${animationStyles}`}>
      {isOpen && (
        <div className="p-4 text-[14px] md:text-[15px] leading-[19.6px] text-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

const FAQSection = ({ content }: { content: string }) => {
  const [faqItems, setFaqItems] = useState<
    Array<{
      heading: string;
      content: string;
      isOpen: boolean;
    }>
  >([]);

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const items: Array<{ heading: string; content: string; isOpen: boolean }> =
      [];

    const headings = doc.querySelectorAll('h3, h4, h5');
    headings.forEach(heading => {
      let content = '';
      let currentElement = heading.nextElementSibling;
      if (
        currentElement &&
        currentElement.tagName === 'P' &&
        currentElement.innerHTML.trim().startsWith('<strong>A')
      ) {
        const cleanedParagraph = currentElement.innerHTML.replace(
          /<strong>A\s*[:\-.:]?\s*<\/strong>/i,
          ''
        );
        content += `<p>${cleanedParagraph}</p>`;
        currentElement = currentElement.nextElementSibling;
      }
      while (
        currentElement &&
        !['H3', 'H4', 'H5'].includes(currentElement.tagName)
      ) {
        content += currentElement.outerHTML;
        currentElement = currentElement.nextElementSibling;
      }
      const cleanedHeading =
        heading.textContent?.replace(/^Q\s*[:\-.:]?\s*/i, '') || '';
      items.push({
        heading: cleanedHeading.trim(),
        content: content.trim(),
        isOpen: false
      });
    });

    setFaqItems(items);
  }, [content]);

  const toggleFAQ = (index: number) => {
    setFaqItems(prevItems =>
      prevItems.map((item, i) => ({
        ...item,
        isOpen: i === index ? !item.isOpen : false
      }))
    );
  };

  return (
    <div className="space-y-4">
      <style jsx global>{`
        @keyframes expand {
          0% {
            transform: scaleY(0);
          }
          80% {
            transform: scaleY(1.1);
          }
          100% {
            transform: scaleY(1);
          }
        }
        .animate-expand {
          animation: expand 500ms linear forwards;
        }
      `}</style>

      {faqItems.map((item, index) => (
        <div key={index} className="border rounded-lg overflow-hidden">
          <div
            className="flex justify-between items-center p-4 bg-gray-50 cursor-pointer"
            onClick={() => toggleFAQ(index)}
          >
            <h3 className="text-[13px] leading-[21px] font-semibold text-[#454545] sm:text-[16px] sm:leading-[18.2px]">
              {item.heading}
            </h3>
            <Image
              src="/plus-mark.svg"
              alt="plus icon"
              width={15}
              height={15}
              className={`w-5 h-5 transition-transform duration-500 ${
                item.isOpen ? 'rotate-45' : 'rotate-0'
              } sm:translate-y-1`}
            />
          </div>

          <ContentAnimation isOpen={item.isOpen}>
            <div
              dangerouslySetInnerHTML={{
                __html: item.content.replace(
                  /<a\s+/g,
                  '<a class="text-blue-500 underline hover:text-blue-700" '
                )
              }}
            />
          </ContentAnimation>
        </div>
      ))}
    </div>
  );
};

export default FAQSection;
